import { addItems, setSelected } from 'apis/list';
import { logAddItemsToFavourites } from 'helpers/analytics';
import { track } from 'helpers/optimizely';
import { FavouritesPayload, publish } from 'helpers/pubsub';
import { refreshCache } from 'list/helpers/store';
import {
    ADD_FAILURE,
    ADD_ITEMS_FAILURE,
    ADD_ITEMS_LOADING,
    ADD_ITEMS_SUCCESS,
    ADD_LOADING,
    ADD_SUCCESS,
} from 'list/pubsub';
import translate from 'list/utils/translate';
import { favouritesUrl } from 'utils/settings';
import { reportError } from 'helpers/sentry';

export const addToList = (
    payload: FavouritesPayload,
    listId?: string,
    name?: string,
    skipToast = true
): Promise<boolean> => {
    let formattedItemsPayload: Array<{
        itemNo: string;
        quantity: number;
        type: string;
    }>;
    if ('items' in payload) {
        formattedItemsPayload = payload.items.map(({ productNumber, productType, quantity }) => ({
            itemNo: productNumber,
            quantity: Number(quantity),
            type: productType,
        }));
    } else {
        formattedItemsPayload = [
            {
                itemNo: payload.productNumber,
                quantity: Number(payload.quantity),
                type: payload.productType,
            },
        ];
    }
    const formattedGraphqlPayload = formattedItemsPayload.map(({ itemNo, quantity }) => ({
        itemNo,
        quantity,
    }));
    if ('items' in payload) {
        publish(ADD_ITEMS_LOADING, payload);
    } else {
        publish(ADD_LOADING, payload);
    }
    return addItems(formattedGraphqlPayload, listId)
        .then(async res => {
            try {
                if (listId) {
                    await setSelected(listId);
                }
                if ('items' in payload) {
                    publish(ADD_ITEMS_SUCCESS, { ...payload, skipToast });
                } else {
                    publish(ADD_SUCCESS, { ...payload, skipToast });
                }
                const toastText =
                    'items' in payload
                        ? translate('Items were added to the list', {
                              count: String(formattedGraphqlPayload.length),
                              listName: name && name !== '' ? name : translate('Default list name'),
                          })
                        : translate('Item was added to the list', {
                              productName: payload.productName,
                              listName: name && name !== '' ? name : translate('Default list name'),
                          });
                const currentListId = listId ? listId : res.data.list.listId;
                window?.ikea?.toast(
                    toastText,
                    {
                        label: translate('Show'),
                        callbackOrHref: `${favouritesUrl}${currentListId}/retail`,
                    },
                    null
                );
                refreshCache();
                track('favourites_item_added_to_list');
                logAddItemsToFavourites(payload.source, currentListId, formattedItemsPayload, payload?.ga);
                return true;
            } catch (e) {
                reportError(e as Error);
                return false;
            }
        })
        .catch(e => {
            reportError(e as Error);
            if ('items' in payload) {
                publish(ADD_ITEMS_FAILURE, { ...payload, skipToast });
            } else {
                publish(ADD_FAILURE, { ...payload, skipToast });
            }

            window?.ikea?.toast(
                e === 'ILLEGAL_QUANTITY' ? translate('ILLEGAL_QUANTITY_ERROR') : translate('GENERIC_ERROR'),
                { label: translate('Show'), callbackOrHref: favouritesUrl },
                null
            );
            return false;
        });
};
